/**
 * Выгрузка таблиц портала в Excel с сохранением вида.
 *
 * Одна точка входа на весь портал: где есть отрисованная таблица, там
 * работает и выгрузка. Никаких отдельных описаний колонок под каждый
 * экран — что показано, то и выгружается, включая объединения, рамки,
 * заливки, шрифты и ширины.
 *
 *     exportTablesToExcel({
 *         fileName: 'kontrol-pps',
 *         sheets: [{ table: ref.current, name: 'Контроль ППС', title, meta }]
 *     })
 */
import { readTable } from './domTable';
import { buildXlsx } from './workbook';
/**
 * Прочитать таблицы при светлой теме и вернуть всё обратно.
 *
 * Тему переключаем атрибутом на html и возвращаем в том же проходе кода.
 * Браузер рисует кадр только между задачами, поэтому мигания на экране не
 * будет, а getComputedStyle внутри уже отдаст светлые значения: обращение
 * к нему само заставляет пересчитать стили.
 */
const withLightTheme = (on, fn) => {
    const root = document.documentElement;
    const before = root.getAttribute('data-theme');
    if (!on || before !== 'dark')
        return fn();
    root.setAttribute('data-theme', 'light');
    try {
        return fn();
    }
    finally {
        if (before === null)
            root.removeAttribute('data-theme');
        else
            root.setAttribute('data-theme', before);
    }
};
/** Заголовок над таблицей — если он есть, им и назовём лист. */
const nearestTitle = (table) => {
    var _a, _b, _c;
    const caption = (_b = (_a = table.caption) === null || _a === void 0 ? void 0 : _a.innerText) === null || _b === void 0 ? void 0 : _b.trim();
    if (caption)
        return caption;
    let node = table.previousElementSibling;
    while (node) {
        if (/^H[1-6]$/.test(node.tagName))
            return node.innerText.trim();
        node = node.previousElementSibling;
    }
    const head = (_c = table.parentElement) === null || _c === void 0 ? void 0 : _c.previousElementSibling;
    if (head && /^H[1-6]$/.test(head.tagName))
        return head.innerText.trim();
    return '';
};
/**
 * Таблицы внутри блока — листами будущей книги.
 *
 * Ссылка на блок удобнее ссылки на таблицу: на экранах, где таблиц две-три
 * подряд или они рисуются в цикле, отдельную ссылку на каждую не завести.
 * Имя листа берём из ближайшего заголовка над таблицей — так в книге видно,
 * что есть что; когда заголовка нет, нумеруем.
 */
export const collectSheets = (root, name, meta) => {
    if (!root)
        return [];
    const tables = root.tagName === 'TABLE'
        ? [root]
        : Array.from(root.querySelectorAll('table'));
    /* Пустых таблиц на листе быть не должно: экран мог отрисовать
       заготовку до того, как пришли данные.

       Помеченное data-noexcel пропускаем — это пара к data-noprint, которую
       понимает печать. Нужна она там, где для бумаги рисуют вторую копию
       той же таблицы: сетку журнала печать режет на листы по четырнадцать
       дат, и без этой пометки книга получала бы и целую сетку, и все её
       куски отдельными листами. */
    const filled = tables.filter(x => x.rows.length > 0 && !x.closest('[data-noexcel]'));
    const used = new Set();
    return filled.map((table, i) => {
        const own = nearestTitle(table);
        /* Имена листов в книге обязаны различаться, и длиннее 31 знака
           Excel их не принимает. */
        let sheet = (filled.length === 1 ? name : `${i + 1}. ${own || name}`).slice(0, 31);
        while (used.has(sheet))
            sheet = `${i + 1}. ${sheet}`.slice(0, 31);
        used.add(sheet);
        return { table, name: sheet, title: own || name, meta };
    });
};
/** Собрать книгу, ничего не сохраняя, — этим же пользуются проверки. */
export const buildWorkbook = (options) => {
    const sources = options.sheets.filter(s => s.table);
    const sheets = withLightTheme(options.forceLightTheme !== false, () => sources.map((s, i) => readTable(s.table, {
        sheetName: s.name || `Лист${i + 1}`,
        title: s.title,
        meta: s.meta
    })));
    return buildXlsx([...(options.extraSheets || []), ...sheets], options.mono !== false);
};
/**
 * Собрать книгу и отдать файл.
 *
 * Возвращает false, если выгружать нечего: вызывающий экран решает сам,
 * сообщать об этом или просто держать кнопку выключенной.
 */
export const exportTablesToExcel = (options) => {
    var _a;
    if (!options.sheets.some(s => s.table) && !((_a = options.extraSheets) === null || _a === void 0 ? void 0 : _a.length))
        return false;
    const bytes = buildWorkbook(options);
    const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${options.fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    /* Ссылку отпускаем позже: часть браузеров начинает скачивание не сразу. */
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    return true;
};
